import React from 'react';
import { FileText, ShieldCheck, Calendar, Users, Check } from 'lucide-react';

/**
 * Shared bits of Enquiries & Assessments — the step before the Enquiry List.
 *
 * Wording follows the organisation: a care home has residents and admissions,
 * a domiciliary service has clients and care starts. Everything else is the
 * same flow.
 */

export const wording = (mode) => {
  const home = mode !== 'domiciliary';
  return {
    home,
    noun: home ? 'resident' : 'client',
    Noun: home ? 'Resident' : 'Client',
    Plural: home ? 'Residents' : 'Clients',
    admission: home ? 'admission' : 'care start',
    Admission: home ? 'Admission' : 'Care start',
    pending: home ? 'Pending admissions' : 'Pending care starts',
    planStage: home ? 'Admission planning' : 'Care package',
  };
};

export const statusLabel = (status, mode) => {
  const w = wording(mode);
  return ({
    NEW: 'New enquiry',
    ASSESSMENT: 'Assessment underway',
    REVIEW: 'Awaiting review',
    SUITABLE: 'Suitable',
    DEFERRED: 'Deferred',
    DECLINED: 'Declined',
    PLANNED: w.home ? 'Planned admission' : 'Start planned',
    CONVERTED: `Converted to ${w.noun}`,
  })[status] || status;
};

const TONE = {
  green: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400',
  amber: 'bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  red: 'bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-400',
  blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
  grey: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

export const statusTone = (status) => (
  ['SUITABLE', 'CONVERTED'].includes(status) ? 'green'
    : ['REVIEW', 'DEFERRED'].includes(status) ? 'amber'
      : status === 'DECLINED' ? 'red' : 'blue'
);

export const Badge = ({ tone = 'blue', children }) => (
  <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[11.5px] font-semibold whitespace-nowrap ${TONE[tone] || TONE.blue}`}>
    {children}
  </span>
);

export const StatusBadge = ({ status, mode }) => (
  <Badge tone={statusTone(status)}>{statusLabel(status, mode)}</Badge>
);

/** Which of the five stages is current. */
export const stageIndex = (status) => ({
  NEW: 0, ASSESSMENT: 1, REVIEW: 2, DEFERRED: 2, DECLINED: 2, SUITABLE: 3, PLANNED: 3, CONVERTED: 4,
})[status] ?? 0;

export const OPEN_STATUSES = ['NEW', 'ASSESSMENT', 'REVIEW', 'DEFERRED', 'SUITABLE'];
export const PENDING_STATUSES = ['PLANNED'];
export const HISTORY_STATUSES = ['CONVERTED', 'DECLINED'];

export const nextAction = (status, mode) => {
  const w = wording(mode);
  return ({
    NEW: 'Start assessment',
    ASSESSMENT: 'Continue assessment',
    REVIEW: 'Review assessment',
    SUITABLE: w.home ? 'Plan admission' : 'Plan care package',
    DEFERRED: 'Reopen assessment',
    DECLINED: 'View decision',
    PLANNED: `Convert to ${w.noun}`,
    CONVERTED: 'View history',
  })[status] || 'Open';
};

export const Journey = ({ status, mode }) => {
  const w = wording(mode);
  const current = stageIndex(status);
  const stages = [
    [FileText, 'Enquiry', 'Capture the essentials'],
    [FileText, 'Assessment', 'Understand care needs'],
    [ShieldCheck, 'Manager review', 'Record suitability'],
    [Calendar, w.planStage, 'Agree the arrangements'],
    [Users, `Active ${w.noun}`, w.home ? 'Confirm arrival' : 'Confirm care has started'],
  ];
  return (
    <section aria-label="Enquiry progress" className="bg-white dark:bg-[#262b33] border border-[#E3E9F3] dark:border-gray-800 rounded-2xl p-5">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-[13px] font-bold text-[#1E293B] dark:text-gray-100">Enquiry progress</h2>
        <span className="hidden md:inline text-[11.5px] text-[#94A3B8]">One person. One connected record.</span>
      </div>
      <ol className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {stages.map(([Icon, title, sub], n) => {
          const done = n < current || (status === 'CONVERTED' && n === 4);
          const active = n === current && status !== 'CONVERTED';
          return (
            <li key={title} className="flex items-center gap-2.5" aria-current={active ? 'step' : undefined}>
              <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border ${active
                ? 'bg-blue-600 border-blue-600 text-white'
                : done ? 'bg-emerald-50 border-emerald-100 text-emerald-600 dark:bg-emerald-900/20 dark:border-emerald-900/40'
                  : 'bg-[#F8FAFF] border-[#E2E8F2] text-[#7F94B3] dark:bg-[#1f242b] dark:border-gray-800'}`}
              >
                {done ? <Check className="w-4 h-4" /> : <Icon className="w-4 h-4" />}
              </span>
              <span className="min-w-0">
                <strong className="block text-[12px] font-semibold text-[#1E293B] dark:text-gray-200">{n + 1}. {title}</strong>
                <small className="block text-[11px] text-[#94A3B8]">{sub}</small>
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
};

export const formatDate = (value) => {
  if (!value) return 'Not recorded';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export const formatDateTime = (value) => {
  if (!value) return 'Not yet';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
};

export const initials = (e) => `${(e?.first_name || '?')[0]}${(e?.surname || '')[0] || ''}`.toUpperCase();

export const errorMessage = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

export const inputCls = 'w-full min-h-[42px] border border-[#D7E1EF] dark:border-gray-700 bg-white dark:bg-[#31373f] rounded-lg px-3 py-2.5 text-[13.5px] text-[#263B56] dark:text-gray-200 placeholder-[#ACB6C5] focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/15';
export const labelCls = 'block text-[12px] font-semibold text-[#536681] dark:text-gray-400 mb-1.5';
export const panelCls = 'bg-white dark:bg-[#262b33] border border-[#E3E9F3] dark:border-gray-800 rounded-2xl overflow-hidden';
